<template>
  <fn-button
    :id="id"
    class="wishlist-button"
    :class="[
      { order: orderStyle },
      { 'pt-old': ptOldStyle },
      { 'pt-old--bestseller': bestseller },
      { 'pt-new': ptNewStyle },
      { atcf: atcfStyle },
    ]"
    data-qa-id="button-wishlist"
    :tertiary="tertiary"
    :icon="wishlistButtonIcon"
    :status="wishlistButtonStatus"
    :disabled="disabled"
    :aria-label="
      isOnWishlist ? $t('accessibility.general.removeFromWishlist') : $t('accessibility.general.addToWishlist')
    "
    @click="handleWishlistItem"
  />
</template>

<script>
import { mapActions, mapState } from 'pinia';

export default defineComponent({
  props: {
    id: {
      type: String,
      default: 'addToWishlist',
    },
    productCode: {
      type: [String, Number],
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    orderStyle: {
      type: Boolean,
      default: false,
    },
    ptOldStyle: {
      type: Boolean,
      default: false,
    },
    ptNewStyle: {
      type: Boolean,
      default: false,
    },
    atcfStyle: {
      type: Boolean,
      default: false,
    },
    tertiary: {
      type: Boolean,
      default: false,
    },
    bestseller: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    status: {
      loading: false,
      addedToWishlist: false,
    },
  }),

  computed: {
    ...mapState(useWishlistStore, {
      onWishlist: 'getOnWishlist',
    }),

    isOnWishlist() {
      if (this.onWishlist(this.productCode)) {
        return true;
      }

      return this.status.addedToWishlist;
    },

    wishlistButtonIcon() {
      return this.isOnWishlist ? 'heart-filled-new' : 'heart-new';
    },

    wishlistButtonStatus() {
      if (this.status.loading && this.ptNewStyle) {
        return 'loading-new';
      }
      return this.status.loading ? 'loading' : '';
    },
  },

  methods: {
    ...mapActions(useWishlistStore, {
      addWishlistItem: 'addWishlistItem',
      removeWishlistItem: 'removeWishlistItem',
    }),

    handleWishlistItem(event) {
      const productInWishlist = this.onWishlist(this.productCode);

      this.status.loading = true;
      this.status.addedToWishlist = !productInWishlist;

      const apiMethod = productInWishlist ? this.removeWishlistItem : this.addWishlistItem;

      apiMethod({ productCode: this.productCode })
        .then(({ status }) => {
          if (status !== 200) {
            throw new Error('Error while adding/removing product to/from wishlist');
          }
        })
        .catch(() => {
          this.status.addedToWishlist = productInWishlist;
        })
        .finally(() => {
          this.status.loading = false;
          event.target.blur();
        });
    },
  },
});
</script>

<style lang="scss" scoped>
.wishlist-button {
  aspect-ratio: 1;

  &.order {
    background-color: $color-general-background-neutral-default;
    border: map-get($border-width, s) solid $color-action-border-neutral-default-base;
    color: $color-action-icons-secondary-default-base;

    &:hover {
      color: $color-action-icons-secondary-default-base;
    }

    &.button--status-loading {
      background: palette(grey, light);
    }

    // Overwrite fn-icon styles
    // Normal and inverted heart icons have weird/not the same dimensions
    .icon {
      position: absolute;
      top: 7px;

      &--heart-inverted {
        color: $color-action-icons-secondary-default-base;
        transform: scale(0.85);
      }
    }
  }

  &.pt-old {
    border-radius: 50%;
    width: 40px;
    height: 40px;
    position: absolute;
    top: 11px;
    right: 11px;
    margin: 0;
    border: map-get($border-width, s) solid $color-action-border-neutral-default-base;
    padding: 0;

    &.pt-old--bestseller {
      right: 10px;
      top: 25px;

      :deep(.icon) {
        color: $color-action-icons-secondary-default-base;
      }
    }

    &.button--status-loading {
      padding: 0 !important;
    }

    &.button--status-added {
      :deep(.icon) {
        top: 7px;
        color: $color-action-icons-secondary-default-base;
      }
    }

    &.button--icon-only {
      padding: 0;
    }

    :deep(.icon) {
      color: $color-action-icons-secondary-default-base;
    }
  }

  &.pt-new {
    padding: 0;
    background: $color-general-background-neutral-default;
    color: palette(orientation, green);
    transition: background 0.3s ease-in-out;

    &.button--icon.button--icon-only {
      padding: 0;

      :deep(.icon) {
        position: static;
      }
    }

    &.button--status-loading-new {
      background-color: palette(orientation, green) !important;
      color: $color-general-text-inverse-default !important;
    }

    &:hover {
      background: $color-general-background-neutral-default;
    }
  }

  &.atcf {
    background-color: transparent;
    border: map-get($border-width, s) solid $color-action-border-neutral-default-base;
    color: $color-action-icons-secondary-default-base;
    padding: 0 6px;

    :deep(.icon) {
      width: 24px;
    }

    &.button--status-loading {
      padding-left: 0;
    }
  }
}
</style>
